import dayjs from "dayjs";
import Decimal from "decimal.js";
import $ from "jquery";
import Snap from "snapsvg-cjs-ts";
import {
    ForecastTime,
    InitialTime,
    ShadeSelector,
    ShadeVariable,
} from "./Controller";
import { Checkbox, InputNumber, Selectbox } from "./Form";
import { readChoice, readFlag, readNumber } from "./UrlState";
import { resourcePath } from "./paths";

export class Clock {
    private static holiday = new Set<string>();
    static async fetch() {
        const holiday = await fetch(
            `${resourcePath("holiday.json")}?${Date.now()}`,
        ).then((res) => res.json());
        for (const date of Object.keys(holiday)) {
            this.holiday.add(date);
        }
    }

    private readonly tintMap = new Map<ShadeVariable, number>([
        ["GSMR", 1], ["GSMRG", 1], ["GSMRC", 1], ["GSMC", 1], ["GSMN", 1], ["GSMSj", 1], ["GSMSh", 1], ["GSMSv", 1],
        ["GS5R", 1], ["GS5Sj", 1], ["GS5Sh", 1], ["GS5N", 1], // GS5は3時間毎だが、時計表示上ここでは1に。
        ["GSAR", 1], ["GSASj", 1], ["GSASh", 1], ["GSASv", 1], ["GSAN", 1],
        ["GSLR", 6], ["GSLN", 6],
        ["MSGL", 1], // MSGLは3時間毎だが、GSAと同じ時刻・時計表記にするため1に (2026-07-22)。
        ["MSMR", 1], ["MSMRG", 1], ["MSMRC", 1], ["MSMN", 1], ["MSMT", 1], ["MSMSj", 1], ["MSMSh", 1], ["MSMSv", 1],
        ["LFMR", 0.5], ["LFMRC", 0.5], ["LFMN", 0.5], ["LFMT", 0.5], ["LFMSj", 0.5], ["LFMSh", 0.5], ["LFMSv", 0.5],
        ["CWMH", 1], // CWMHは3時間毎だが、時計表示上ここでは1に。
        ["QMAR", 1], ["QMAN", 1], ["QMASj", 1], ["QMASh", 1],
        ["GSMV", 3],
        ["MSMV", 3],
    ]); // biome-ignore format:

    private readonly hourJ = new Map<number, string>([
        [3, "未明"],
        [6, "明け方"],
        [9, "朝"],
        [12, "昼前"],
        [15, "昼過ぎ"],
        [18, "夕方"],
        [21, "夜"],
        [0, "夜遅く"],
    ]);
    private readonly gpvNhHourJ = new Map<number, string>([
        [3, "未明"],
        [9, "朝"],
        [15, "昼"],
        [21, "夜"],
    ]);

    private x = 45;
    private y = 40;
    private scale = 1;
    private x0 = 0;
    private y0 = 0;

    private readonly size = new InputNumber("#clock_size", {
        min: 0,
        max: 1.3,
        step: 0.1,
    });
    private readonly opacity = new InputNumber("#clock_opacity", {
        min: 0,
        max: 1,
        step: 0.1,
    });
    private readonly displayClock = new Checkbox("#clock_display-clock");
    private readonly useNoon = new Checkbox("#clock_use-noon");
    private readonly useToday = new Checkbox("#clock_use-today");
    private clockPreference: "0" | "1" = "1";

    // 時計画像は必要になった分だけdata URL化する。初期表示は現在時刻の1枚、
    // 動画/ZIPは対象FTの必要分を開始前に待ち、残りは初回描画後のidleで読む。
    private readonly imageCache = new Map<string, string>();
    private readonly imagePromises = new Map<string, Promise<string | null>>();
    private clockHref = "";
    private currentClockPath: string | null = null;
    private currentImageReady: Promise<void> = Promise.resolve();
    private idlePreloadStarted = false;

    private readonly OV: Snap.Paper;
    private readonly transG: Snap.Paper;
    private readonly scaleG: Snap.Paper;
    private readonly base: Snap.Paper;
    private readonly clock: Snap.Paper;
    private readonly tstitleBack: Snap.Paper;
    private readonly thunderText: Snap.Paper;
    private readonly thunder: Snap.Paper;
    private readonly ddText: TwoDigits;
    private readonly nichiText: OpenFace;
    private readonly dayText: OpenFace;
    private readonly timeText: OpenFace;
    private readonly gslMonthText: TwoDigits;
    private readonly gslTsukiText: OpenFace;
    private readonly gslDayOfMonthText: TwoDigits;
    private readonly gslNichiText: OpenFace;
    private readonly gslWeekdayText: OpenFace;
    private readonly gslTimeText: OpenFace;
    private readonly ampmText: OpenFace;
    private readonly hourText: OpenFace;
    private readonly jiText: OpenFace;
    private readonly minText: OpenFace;
    private readonly funText: OpenFace;
    private readonly noonText: OpenFace;
    private readonly todayText: [OpenFace, OpenFace, OpenFace, OpenFace];
    private readonly gslRelText: [OpenFace, OpenFace, OpenFace, OpenFace];

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
    ) {
        this.size.onChange(() => this.updateSize());
        this.opacity.onChange(() => this.updateOpacity());
        this.displayClock.onChange(() => {
            this.clockPreference = this.displayClock.value;
            this.update();
        });
        this.useNoon.onChange(() => this.update());
        this.useToday.onChange(() => this.update());

        this.size.value = 1;
        this.opacity.value = 0.9;
        this.clockPreference = "1";
        this.displayClock.value = this.clockPreference;
        this.useNoon.value = "0";
        // 現行版の「日付」checkboxは、checkedで相対日付を使わない。
        this.useToday.value = "0";

        this.loadParams();

        const OV = Snap("#overlay");
        this.OV = OV;

        this.transG = OV.g()
            .attr({
                id: "fortime",
                strokeLinecap: "round",
                "stroke-linejoin": "round",
            })
            .drag(
                (dx, dy) => this.dragMove(dx, dy),
                () => this.dragStart(),
                () => this.dragEnd(),
            )
            .transform(`translate(${this.x},${this.y})`) as Snap.Paper;

        this.scaleG = this.transG.g() as Snap.Paper;

        this.base = this.scaleG
            .rect(0, 0, 560, 240)
            .attr({ fill: "#ffffff", stroke: "none", rx: 16 }) as Snap.Paper;

        this.clock = this.scaleG
            .image("", 0, 10, 225, 225)
            .attr({ id: "clock_image" }) as Snap.Paper;

        this.tstitleBack = this.scaleG.rect(0, 257, 560, 101).attr({
            fill: "#000000",
            stroke: "none",
            rx: 16,
        }) as Snap.Paper;

        this.thunderText = this.scaleG.text(280, 310, "発雷確率").attr({
            fontSize: 70,
            fill: "#ffffff",
            letterSpacing: 10,
            textAnchor: "middle",
            dominantBaseline: "central",
        }) as Snap.Paper;

        this.thunder = this.scaleG.image(
            "img/thunder.png",
            405,
            220,
            200,
            200,
        ) as Snap.Paper;

        this.ddText = new TwoDigits(310, 100, 0, {});
        this.ddText.appendTo(this.scaleG);

        this.nichiText = new OpenFace(365, 100, "日", { textAnchor: "start" });
        this.nichiText.appendTo(this.scaleG);

        this.dayText = new OpenFace(495, 100, "", {
            textAnchor: "middle",
            fontSize: 50,
            class: "normal-weekday-text",
        });
        this.dayText.appendTo(this.scaleG);

        this.timeText = new OpenFace(390, 200, "", { textAnchor: "middle" });
        this.timeText.appendTo(this.scaleG);

        // GPV-NHのGSMN/GSMLと同じ固定位置・文字サイズの日時表示。
        // 日付と時間帯を別要素にして、時間帯の字数で日付位置を動かさない。
        this.gslMonthText = new TwoDigits(60, 85, 0, {
            class: "gsl-date-part gsl-month-digits",
        });
        this.gslMonthText.appendTo(this.scaleG);

        this.gslTsukiText = new OpenFace(115, 85, "月", {
            textAnchor: "start",
            fontSize: 50,
            class: "gsl-date-part gsl-month-unit",
        });
        this.gslTsukiText.appendTo(this.scaleG);

        this.gslDayOfMonthText = new TwoDigits(215, 85, 0, {
            class: "gsl-date-part gsl-day-digits",
        });
        this.gslDayOfMonthText.appendTo(this.scaleG);

        this.gslNichiText = new OpenFace(270, 85, "日", {
            textAnchor: "start",
            fontSize: 50,
            class: "gsl-date-part gsl-day-unit",
        });
        this.gslNichiText.appendTo(this.scaleG);

        this.gslWeekdayText = new OpenFace(380, 85, "", {
            textAnchor: "middle",
            fontSize: 60,
            class: "gsl-date-part gsl-weekday-text",
        });
        this.gslWeekdayText.appendTo(this.scaleG);

        this.gslTimeText = new OpenFace(555, 85, "", {
            textAnchor: "middle",
            class: "gsl-time-text",
        });
        this.gslTimeText.appendTo(this.scaleG);

        this.ampmText = new OpenFace(370, 200, "", { textAnchor: "end" });
        this.ampmText.appendTo(this.scaleG);

        this.hourText = new OpenFace(420, 200, "", { textAnchor: "middle" });
        this.hourText.appendTo(this.scaleG);

        this.jiText = new OpenFace(470, 200, "時", { textAnchor: "start" });
        this.jiText.appendTo(this.scaleG);

        this.minText = new OpenFace(458, 200, "", { textAnchor: "middle" });
        this.minText.appendTo(this.scaleG);

        this.funText = new OpenFace(510, 200, "分", {
            textAnchor: "start",
            fontSize: 40,
        });
        this.funText.appendTo(this.scaleG);

        this.noonText = new OpenFace(390, 200, "正　午", {
            textAnchor: "middle",
        });
        this.noonText.appendTo(this.scaleG);

        // letter-spacing は末尾の字間も送り幅に含まれ text-anchor:middle の
        // 中央が左へずれるため、字間の半分 (em×70/2) だけアンカーを右へ補正する
        // 添字は d(きょうとの日差)+1: [きのう, きょう, あす, あさって]
        this.todayText = [
            new OpenFace(340 + (0.15 * 70) / 2, 100, "きのう", {
                textAnchor: "middle",
                letterSpacing: "0.15em",
            }),
            new OpenFace(340 + (0.15 * 70) / 2, 100, "きょう", {
                textAnchor: "middle",
                letterSpacing: "0.15em",
            }),
            new OpenFace(340 + (0.55 * 70) / 2, 100, "あす", {
                textAnchor: "middle",
                letterSpacing: "0.55em",
            }),
            new OpenFace(340 + (-0.05 * 70) / 2, 100, "あさって", {
                textAnchor: "middle",
                letterSpacing: "-0.05em",
                transform: "s 0.9,1",
            }),
        ];
        for (const text of this.todayText) {
            text.appendTo(this.scaleG);
        }

        // GSL の日付部 (月日) を相対日付で置き換える語。位置は日付部の中央。
        this.gslRelText = [
            new OpenFace(170 + (0.15 * 70) / 2, 85, "きのう", {
                textAnchor: "middle",
                letterSpacing: "0.15em",
                class: "gsl-date-part gsl-rel-text",
            }),
            new OpenFace(170 + (0.15 * 70) / 2, 85, "きょう", {
                textAnchor: "middle",
                letterSpacing: "0.15em",
                class: "gsl-date-part gsl-rel-text",
            }),
            new OpenFace(170 + (0.55 * 70) / 2, 85, "あす", {
                textAnchor: "middle",
                letterSpacing: "0.55em",
                class: "gsl-date-part gsl-rel-text",
            }),
            new OpenFace(170 + (-0.05 * 70) / 2, 85, "あさって", {
                textAnchor: "middle",
                letterSpacing: "-0.05em",
                transform: "s 0.9,1",
                class: "gsl-date-part gsl-rel-text",
            }),
        ];
        for (const text of this.gslRelText) {
            text.appendTo(this.scaleG);
        }

        this.update();
        this.updateOpacity();
        this.updateSize();
    }
    private loadParams() {
        this.x = readNumber(this.params, "ft_x", 45, -10000, 10000);
        this.y = readNumber(this.params, "ft_y", 40, -10000, 10000);
        this.size.value = readNumber(this.params, "ft_size", 1, 0, 1.3);
        this.opacity.value = readNumber(this.params, "ft_opacity", 0.9, 0, 1);
        this.clockPreference = readFlag(this.params, "ft_clock", "1");
        this.displayClock.value = this.clockPreference;
        this.useNoon.value = readFlag(this.params, "ft_noon", "0");
        this.useToday.value =
            readFlag(this.params, "ft_today", "1") === "1" ? "0" : "1";
    }
    applyParams() {
        this.loadParams();
        this.transG.transform(`translate(${this.x},${this.y})`);
        this.updateOpacity();
        this.updateSize();
        return this.update();
    }
    saveParams() {
        this.params.set("ft_x", this.x.toString());
        this.params.set("ft_y", this.y.toString());
        this.params.set("ft_size", this.size.value.toString());
        this.params.set("ft_opacity", this.opacity.value.toString());
        this.params.set("ft_clock", this.clockPreference);
        this.params.set("ft_noon", this.useNoon.value);
        this.params.set("ft_today", this.useToday.value === "1" ? "0" : "1");
    }
    private imagePaths() {
        const paths: string[] = [];
        for (let h = 0; h < 24; h += 3) {
            paths.push(`img/time3/time3_${String(h).padStart(2, "0")}.png`);
        }
        for (let h = 0; h < 24; h++) {
            const hh = String(h).padStart(2, "0");
            paths.push(`img/time1/time1_${hh}.png`);
            paths.push(`img/time0.5/time0.5_${hh}.0.png`);
            paths.push(`img/time0.5/time0.5_${hh}.5.png`);
        }
        return paths;
    }
    private loadImage(path: string) {
        const cached = this.imageCache.get(path);
        if (cached) {
            return Promise.resolve(cached);
        }
        const pending = this.imagePromises.get(path);
        if (pending) {
            return pending;
        }
        const promise = fetch(path)
            .then((res) => (res.ok ? res.blob() : null))
            .then(
                (blob) =>
                    new Promise<string | null>((resolve) => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => resolve(String(reader.result));
                        reader.onerror = () => resolve(null);
                        reader.readAsDataURL(blob);
                    }),
            )
            .catch(() => null)
            .then((dataUrl) => {
                if (dataUrl) {
                    this.imageCache.set(path, dataUrl);
                } else {
                    // 一時失敗を固定しない。表示時・出力時に再試行できるようにする。
                    this.imagePromises.delete(path);
                }
                return dataUrl;
            });
        this.imagePromises.set(path, promise);
        return promise;
    }
    private pathForFt(ft: Decimal) {
        const { shadeVariable } = this.shadeSelector.getParams();
        if (shadeVariable.startsWith("GSL")) {
            return null;
        }
        const tint = this.tintMap.get(shadeVariable)!;
        const { ini } = this.initialTime.getParams();
        const valid = ini.add(ft.mul(60).toNumber(), "minutes");
        if (tint < 1) {
            const half = valid.minute() === 0 ? "0" : "5";
            return `img/time0.5/time0.5_${valid.format("HH")}.${half}.png`;
        }
        return `img/time${tint}/time${tint}_${valid.format("HH")}.png`;
    }
    private updateClockImage(path: string | null) {
        this.currentClockPath = path;
        if (!path || this.clockPreference !== "1") {
            this.currentImageReady = Promise.resolve();
            return this.currentImageReady;
        }
        const cached = this.imageCache.get(path);
        const href = cached ?? path;
        if (href !== this.clockHref) {
            this.clockHref = href;
            this.clock.attr({ "xlink:href": href });
        }
        this.currentImageReady = this.loadImage(path).then((dataUrl) => {
            if (
                dataUrl &&
                this.currentClockPath === path &&
                dataUrl !== this.clockHref
            ) {
                this.clockHref = dataUrl;
                this.clock.attr({ "xlink:href": dataUrl });
            }
        });
        return this.currentImageReady;
    }
    prepareForecastTimes(fts: Decimal[]) {
        if (this.clockPreference !== "1") {
            return Promise.resolve();
        }
        const paths = new Set(
            fts
                .map((ft) => this.pathForFt(ft))
                .filter((path): path is string => path !== null),
        );
        return Promise.all([...paths].map((path) => this.loadImage(path))).then(
            () => undefined,
        );
    }
    preloadRemainingWhenIdle() {
        if (this.idlePreloadStarted) {
            return;
        }
        this.idlePreloadStarted = true;
        const paths = this.imagePaths();
        let index = 0;
        const runBatch = () => {
            const batch = paths.slice(index, index + 8);
            index += batch.length;
            void Promise.all(batch.map((path) => this.loadImage(path))).then(
                () => {
                    if (index < paths.length) {
                        schedule();
                    }
                },
            );
        };
        const schedule = () => {
            const requestIdle = (
                window as unknown as {
                    requestIdleCallback?: (
                        callback: () => void,
                        options: { timeout: number },
                    ) => number;
                }
            ).requestIdleCallback;
            if (requestIdle) {
                requestIdle(runBatch, { timeout: 2000 });
            } else {
                window.setTimeout(runBatch, 250);
            }
        };
        schedule();
    }
    update() {
        const { shadeVariable } = this.shadeSelector.getParams();
        const tint = this.tintMap.get(shadeVariable)!;
        const isLFM = tint < 1;
        const isGsl = shadeVariable.startsWith("GSL");
        const clockTint = tint;
        const isThunder = ["GSMV", "MSMV"].includes(shadeVariable);

        const clockInput = document.querySelector<HTMLInputElement>(
            "#clock_display-clock",
        )!;
        clockInput.disabled = isGsl;
        clockInput.checked = isGsl ? false : this.clockPreference === "1";

        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        const valid = ini.add(ft.mul(60).toNumber(), "minutes");
        const validDate =
            tint === 3
                ? valid.subtract(3, "hours").hour(0).minute(0)
                : valid.hour(0).minute(0); // FTは後ろの時刻を見ているため，夜遅くの日付は前日にする
        const today = dayjs().hour(0).minute(0).second(0).millisecond(0);

        if (isGsl) {
            this.updateClockImage(null);
            this.updateGslDate(valid, today);
            return this.currentImageReady;
        }
        for (const text of this.gslDateFields()) {
            text.attr({ display: "none" });
        }
        for (const text of this.gslRelText) {
            text.attr({ display: "none" });
        }
        this.gslTimeText.attr({ display: "none" });

        const clockPath = this.pathForFt(ft)!;
        this.clock.attr({ x: isLFM ? 0 : 10 });
        const imageReady = this.updateClockImage(clockPath);

        this.ddText.value(validDate.date());
        this.dayText
            .value(`(${validDate.format("ddd")})`)
            .attr({ display: "inline" });

        if (tint === 3) {
            this.timeText
                .value(this.hourJ.get(valid.hour())!)
                .attr({ display: "inline" });
        } else {
            this.timeText.attr({ display: "none" });
        }

        this.ampmText
            .attr({
                x: isLFM ? 270 : 370,
                display: clockTint < 3 ? "inline" : "none",
                fontSize: isLFM ? 40 : 70,
            })
            .value(valid.hour() === valid.hour() % 12 ? "午前" : "午後");
        this.hourText.value(`${valid.hour() % 12}`).attr({
            x: isLFM ? 315 : 420,
            display: clockTint < 3 ? "inline" : "none",
        });

        this.jiText.attr({
            x: isLFM ? 362 : 470,
            fontSize: isLFM ? 40 : 70,
            display: clockTint < 3 ? "inline" : "none",
        });

        this.minText
            .attr({ display: isLFM ? "inline" : "none" })
            .value(valid.format("mm"));
        this.funText.attr({ display: isLFM ? "inline" : "none" });

        this.thunder.attr({ display: isThunder ? "inline" : "none" });
        this.thunderText.attr({ display: isThunder ? "inline" : "none" });
        this.tstitleBack.attr({ display: isThunder ? "inline" : "none" });

        //時計無し対応
        if (this.clockPreference === "1") {
            this.clock.attr({ display: "inline" });
            this.base.attr({ x: 0, y: 0, width: 560, height: 240 });
            this.tstitleBack.attr({ x: 0, width: 560 });
            this.thunderText.attr({ x: 280 });
            this.thunder.attr({ x: 405 });
        } else {
            this.clock.attr({ display: "none" });
            this.base.attr({ x: 210, y: 10, width: 350, height: 230 });
            this.tstitleBack.attr({ x: 210, width: 350 });
            this.thunderText.attr({ x: 385 });
            this.thunder.attr({ x: -300 }); //画面外
        }

        //正午対応
        if (
            valid.hour() === 12 &&
            valid.minute() === 0 &&
            this.useNoon.value === "1"
        ) {
            this.ampmText.attr({ display: "none" });
            this.hourText.attr({ display: "none" });
            this.jiText.attr({ display: "none" });
            this.minText.attr({ display: "none" });
            this.funText.attr({ display: "none" });
            this.noonText.attr({
                display: clockTint !== 3 ? "inline" : "none",
            });
        } else {
            this.noonText.attr({ display: "none" });
        }

        //日付表記対応 (きのう〜あさって、添字は d+1)
        const d = validDate.diff(today, "days");
        if (this.useToday.value === "0" && d >= -1 && d < 3) {
            this.ddText.attr({ display: "none" });
            this.nichiText.attr({ display: "none" });
            this.todayText.forEach((text, i) => {
                text.attr({ display: d + 1 === i ? "inline" : "none" });
            });
        } else {
            this.ddText.attr({ display: "inline" });
            this.nichiText.attr({ display: "inline" });
            for (const text of this.todayText) {
                text.attr({ display: "none" });
            }
        }

        //土日祝日の色
        const dow = validDate.day();
        const SUNDAY = 0;
        const SATURDAY = 6;
        const isHoliday = Clock.holiday.has(validDate.format("YYYY-MM-DD"));
        const dateTexts = [
            this.ddText,
            this.nichiText,
            this.dayText,
            ...this.todayText,
        ];
        if (dow === SUNDAY || isHoliday) {
            for (const text of dateTexts) {
                text.attr({}, { fill: "#fcc" }, { stroke: "#c00" });
            }
        } else if (dow === SATURDAY) {
            for (const text of dateTexts) {
                text.attr({}, { fill: "#ccf" }, { stroke: "#00c" });
            }
        } else {
            for (const text of dateTexts) {
                text.attr({}, { fill: "#fff" }, { stroke: "#031196" });
            }
        }
        return imageReady;
    }
    private updateGslDate(valid: dayjs.Dayjs, today: dayjs.Dayjs) {
        this.clock.attr({ display: "none" });
        this.base.attr({ x: 0, y: 0, width: 680, height: 115 });
        this.tstitleBack.attr({ display: "none" });
        this.thunderText.attr({ display: "none" });
        this.thunder.attr({ display: "none" });

        for (const text of [
            this.ddText,
            this.nichiText,
            this.dayText,
            this.timeText,
            this.ampmText,
            this.hourText,
            this.jiText,
            this.minText,
            this.funText,
            this.noonText,
            ...this.todayText,
        ]) {
            text.attr({ display: "none" });
        }

        // 日付OFF (相対日付) はきのう〜あさってを月日の代わりに表示する (添字 d+1)。
        const d = valid.hour(0).minute(0).diff(today, "days");
        const useRelative = this.useToday.value === "0" && d >= -1 && d < 3;
        const monthDayFields = [
            this.gslMonthText,
            this.gslTsukiText,
            this.gslDayOfMonthText,
            this.gslNichiText,
        ];
        if (useRelative) {
            for (const text of monthDayFields) {
                text.attr({ display: "none" });
            }
            this.gslRelText.forEach((text, i) => {
                text.attr({ display: d + 1 === i ? "inline" : "none" });
            });
        } else {
            this.gslMonthText.value(valid.month() + 1);
            this.gslDayOfMonthText.value(valid.date());
            for (const text of monthDayFields) {
                text.attr({ display: "inline" });
            }
            for (const text of this.gslRelText) {
                text.attr({ display: "none" });
            }
        }
        this.gslWeekdayText
            .value(`（${valid.format("ddd")}）`)
            .attr({ display: "inline" });
        this.gslTimeText
            .value(this.gpvNhHourJ.get(valid.hour()) ?? valid.format("HH時"))
            .attr(
                { display: "inline" },
                { fill: "#fff" },
                { fill: "#031196", stroke: "#031196" },
            );

        const isHoliday = Clock.holiday.has(valid.format("YYYY-MM-DD"));
        const dateFields = [...this.gslDateFields(), ...this.gslRelText];
        if (valid.day() === 0 || isHoliday) {
            for (const text of dateFields) {
                text.attr({}, { fill: "#fcc" }, { stroke: "#c00" });
            }
        } else if (valid.day() === 6) {
            for (const text of dateFields) {
                text.attr({}, { fill: "#ccf" }, { stroke: "#00c" });
            }
        } else {
            for (const text of dateFields) {
                text.attr({}, { fill: "#fff" }, { stroke: "#031196" });
            }
        }
    }
    private gslDateFields() {
        return [
            this.gslMonthText,
            this.gslTsukiText,
            this.gslDayOfMonthText,
            this.gslNichiText,
            this.gslWeekdayText,
        ];
    }
    private updateOpacity() {
        this.base.attr({ fillOpacity: this.opacity.value });
        this.tstitleBack.attr({ fillOpacity: this.opacity.value });
    }
    private updateSize() {
        this.scaleG.transform(`scale(${this.size.value})`);
    }
    private dragStart() {
        const vb = this.OV.attr("viewBox");
        this.scale = vb.width / Number($("#svg_main").attr("width"));
        const tf = this.transG.transform().local;
        this.x0 = Number(tf.split(",")[0].slice(1));
        this.y0 = Number(tf.split(",")[1]);
    }
    private dragMove(dx: number, dy: number) {
        this.x = this.x0 + dx * this.scale;
        this.y = this.y0 + dy * this.scale;
        this.transG.transform(`translate(${this.x},${this.y})`);
    }
    private dragEnd() {}
    usePreset(preset: string) {
        switch (preset) {
            case "light":
            case "darkwind":
            case "thunder":
                this.size.value = 1;
                this.updateSize();
                break;
            case "trans":
                this.size.value = 0;
                this.updateSize();
                break;
        }
    }
}

type Attr = { [key: string]: string | number };

class OpenFace {
    private readonly fore: Snap.Paper;
    private readonly back: Snap.Paper;
    private readonly g: Snap.Paper;
    constructor(x: number, y: number, str: string, attr: Attr) {
        const fattr = { fill: "white", stroke: "none", fontSize: 70 };
        const battr = {
            fill: "#031196",
            stroke: "#031196",
            strokeWidth: 10,
            fontSize: 70,
        };
        Object.assign(fattr, attr);
        Object.assign(battr, attr);
        const OV = Snap("#overlay");
        this.g = OV.g() as Snap.Paper;
        this.fore = OV.text(x, y, str).attr(fattr) as Snap.Paper;
        this.back = OV.text(x, y, str).attr(battr) as Snap.Paper;
        this.back.appendTo(this.g);
        this.fore.appendTo(this.g);
    }
    attr(attr: Attr, fattr: Attr = {}, battr: Attr = {}) {
        this.fore.attr(Object.assign({}, attr, fattr));
        this.back.attr(Object.assign({}, attr, battr));
        return this;
    }
    value(text: string) {
        this.fore.attr({ text });
        this.back.attr({ text });
        return this;
    }
    appendTo(element: Snap.Paper) {
        this.g.appendTo(element);
    }
}

class TwoDigits {
    private readonly ones: OpenFace;
    private readonly tens: OpenFace;
    private readonly g: Snap.Paper;
    constructor(
        x: number,
        y: number,
        int: number,
        attr: Attr,
        offset: number = 25,
    ) {
        const ones = int % 10;
        const tens = (int - ones) / 10;
        const onesStr = ones.toString();
        const tensStr = tens === 0 ? "" : tens.toString();
        const onesAttr = Object.assign({ textAnchor: "middle" }, attr);
        const tensAttr = Object.assign({ textAnchor: "end" }, attr);
        this.g = Snap("#overlay").g() as Snap.Paper;
        this.ones = new OpenFace(x + offset, y, onesStr, onesAttr);
        this.tens = new OpenFace(x, y, tensStr, tensAttr);
        this.ones.appendTo(this.g);
        this.tens.appendTo(this.g);
    }
    value(int: number) {
        const ones = int % 10;
        const tens = (int - ones) / 10;
        this.ones.value(ones.toString());
        this.tens.value(tens === 0 ? "" : tens.toString());
        return this;
    }
    attr(attr: Attr, fattr: Attr = {}, battr: Attr = {}) {
        this.ones.attr(attr, fattr, battr);
        this.tens.attr(attr, fattr, battr);
        return this;
    }
    appendTo(element: Snap.Paper) {
        this.g.appendTo(element);
    }
}

export class Credit {
    private x = 1906;
    private y = 1068;
    private scale = 1;
    private x0 = 0;
    private y0 = 0;

    private readonly OV: Snap.Paper;
    private readonly g: Snap.Paper;
    private readonly image: Snap.Paper;

    private readonly credit = new Selectbox("#select_credit");

    constructor(private readonly params: URLSearchParams) {
        this.credit.onChange(() => this.update());
        this.credit.value = "";

        this.loadParams();

        this.OV = Snap("#overlay");
        this.g = this.OV.g()
            .attr({ cursor: "default" })
            .transform(`translate(${this.x},${this.y})`)
            .drag(
                (dx, dy) => this.dragMove(dx, dy),
                () => this.dragStart(),
                () => this.dragEnd(),
            ) as Snap.Paper;
        this.image = this.g.image("", 0, 0, 200, 200) as Snap.Paper;

        this.update();
    }
    private loadParams() {
        this.x = readNumber(this.params, "cre_x", 1906, -10000, 10000);
        this.y = readNumber(this.params, "cre_y", 1068, -10000, 10000);
        this.credit.value = readChoice(
            this.params,
            "cre_type",
            ["", "color", "black", "gray1", "gray2"] as const,
            "",
        );
    }
    async applyParams() {
        this.loadParams();
        this.g.transform(`translate(${this.x},${this.y})`);
        await this.update();
    }
    saveParams() {
        this.params.set("cre_x", this.x.toString());
        this.params.set("cre_y", this.y.toString());
        this.params.set("cre_type", this.credit.value);
    }
    private async update() {
        if (this.credit.value === "") {
            this.g.attr({ display: "none" });
            return;
        }
        this.g.attr({ display: "inline", id: "credit" });
        const src = `./common-img/WM_logo_${this.credit.value}.png`;
        const image = await loadImage(src);
        this.image.attr({
            "xlink:href": image.src,
            x: -image.naturalWidth,
            y: -image.naturalHeight,
            width: image.naturalWidth,
            height: image.naturalHeight,
        });
    }
    private dragStart() {
        const vb = this.OV.attr("viewBox");
        this.scale = vb.width / Number($("#svg_main").attr("width"));
        const tf = this.g.transform().local;
        this.x0 = Number(tf.split(",")[0].slice(1));
        this.y0 = Number(tf.split(",")[1]);
    }
    private dragMove(dx: number, dy: number) {
        this.x = this.x0 + dx * this.scale;
        this.y = this.y0 + dy * this.scale;
        this.g.transform(`translate(${this.x},${this.y})`);
    }
    private dragEnd() {}
}

function loadImage(src: string) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = reject;
    });
}
